import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  EMPTY_MY_INPUTS_PROFILE,
  LegacyMyInputsFallback,
  MedicationEntry,
  MyInputsCurrentProfile,
  MyInputsOverview,
} from '../types/myInputs';

function buildLegacyMedicationEntry(name: string, index: number): MedicationEntry {
  return {
    id: `legacy-${index}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    name,
    category: 'other',
    status: 'current',
    memberConfidence: 'approximate',
    source: 'member_entered',
  };
}

export function hydrateLegacyMyInputsProfile(legacy?: LegacyMyInputsFallback | null): MyInputsCurrentProfile {
  if (!legacy?.medications?.length) {
    return { ...EMPTY_MY_INPUTS_PROFILE };
  }

  return {
    ...EMPTY_MY_INPUTS_PROFILE,
    medications: legacy.medications.map((name, index) => buildLegacyMedicationEntry(name, index)),
  };
}

export function normalizeMyInputsProfile(
  profile?: Partial<MyInputsCurrentProfile> | null,
  legacy?: LegacyMyInputsFallback | null,
): MyInputsCurrentProfile {
  const fallback = hydrateLegacyMyInputsProfile(legacy);

  return {
    medications: profile?.medications || fallback.medications || [],
    supplements: profile?.supplements || [],
    dietPatterns: {
      ...EMPTY_MY_INPUTS_PROFILE.dietPatterns,
      ...profile?.dietPatterns,
    },
    exposures: {
      ...EMPTY_MY_INPUTS_PROFILE.exposures,
      ...profile?.exposures,
    },
    updatedAt: profile?.updatedAt || fallback.updatedAt,
    updatedBy: profile?.updatedBy || fallback.updatedBy,
  };
}

export async function getMyInputsProfile(userId: string): Promise<MyInputsCurrentProfile | null> {
  const [profileSnap, userSnap] = await Promise.all([
    getDoc(doc(db, 'users', userId, 'my_inputs', 'current_profile')),
    getDoc(doc(db, 'users', userId)),
  ]);

  const legacy = userSnap.exists() ? (userSnap.data() as LegacyMyInputsFallback) : null;
  if (!profileSnap.exists() && !legacy?.medications?.length) return null;

  return normalizeMyInputsProfile(
    profileSnap.exists() ? (profileSnap.data() as Partial<MyInputsCurrentProfile>) : null,
    legacy,
  );
}

export async function saveMyInputsProfile(
  userId: string,
  profile: MyInputsCurrentProfile,
  updatedBy: MyInputsCurrentProfile['updatedBy'] = 'member',
): Promise<MyInputsCurrentProfile> {
  const normalized = normalizeMyInputsProfile(profile);
  await setDoc(
    doc(db, 'users', userId, 'my_inputs', 'current_profile'),
    {
      ...normalized,
      updatedBy,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  return {
    ...normalized,
    updatedBy,
    updatedAt: new Date(),
  };
}

function formatRelativeUpdatedLabel(updatedAt?: unknown) {
  if (!updatedAt) return 'Not updated yet';
  const value = (updatedAt as { toDate?: () => Date }).toDate?.() ?? new Date(updatedAt as string);
  if (Number.isNaN(value.getTime())) return 'Updated recently';
  const diffMs = Date.now() - value.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Updated today';
  if (diffDays === 1) return 'Updated yesterday';
  if (diffDays < 30) return `Updated ${diffDays} days ago`;
  return `Updated ${value.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

export function summarizeDietPatterns(profile: MyInputsCurrentProfile | null): string {
  if (!profile) return 'No diet profile yet';

  const patterns: string[] = [];
  if (profile.dietPatterns.vegetarianOrVegan && profile.dietPatterns.vegetarianOrVegan !== 'none') {
    patterns.push(profile.dietPatterns.vegetarianOrVegan);
  }
  if (profile.dietPatterns.oilyFishFrequency === 'several_times_weekly') {
    patterns.push('frequent oily fish');
  } else if (profile.dietPatterns.oilyFishFrequency === 'weekly') {
    patterns.push('weekly oily fish');
  }
  if (profile.dietPatterns.alcoholPattern === 'frequent') {
    patterns.push('frequent alcohol');
  }
  if (profile.dietPatterns.caffeinePattern === 'high') {
    patterns.push('high caffeine');
  }
  if (profile.dietPatterns.fiberFocus === 'high') {
    patterns.push('high-fibre focus');
  }

  return patterns.length > 0 ? patterns.join(' • ') : 'No major dietary pattern recorded';
}

export function buildMyInputsOverview(profile: MyInputsCurrentProfile | null): MyInputsOverview {
  const currentMedicationCount = profile?.medications.filter((entry) => entry.status === 'current' || entry.status === 'as_needed').length ?? 0;
  const currentSupplementCount = profile?.supplements.filter((entry) => entry.status === 'current' || entry.status === 'occasional').length ?? 0;

  return {
    currentMedicationCount,
    currentSupplementCount,
    dietSummary: summarizeDietPatterns(profile),
    updatedLabel: formatRelativeUpdatedLabel(profile?.updatedAt),
  };
}
